import {
  OPERATOR_NAME_MAX,
  type OperatorPasskeysResponse,
  operatorDisplayNameSchema,
  type Passkey,
} from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InlineError, Notice } from "@/components/Notice";
import { Page } from "@/components/Page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "../api.ts";
import { ApiError, needsReauth, noContent, ok } from "../api-errors.ts";
import { PasskeysPanel, passkeyName } from "../security/PasskeysPanel.tsx";
import { usePasskeyAdd } from "../security/passkey-add.ts";
import { StepUpForm, StepUpFrame, useSecureAction } from "../security/step-up.tsx";
import { PageSection, SubHeading } from "../settings-shell/PageSection.tsx";
import { SettingsShell } from "../settings-shell/SettingsShell.tsx";
import { createPasskey } from "../shell/webauthn.ts";
import { useCsrfHeaders, useOperator } from "./operator.tsx";
import { useOperatorStepUp } from "./operator-step-up.ts";
import { consoleKeys } from "./queries.ts";

const account = api.api.operator.account;

/**
 * The signed-in operator's profile (#238, from the operator menu): who they are (#391), their
 * authenticator and their passkeys, on the tenant account page's components. The passkeys show
 * without a step-up (#389); adding or removing one needs a sign-in or step-up of the last 5
 * minutes: removing takes a passkey step-up too (#405), adding only the password and
 * authenticator code. A passkey then signs in to the console on its own. Only the operator's own
 * routes are called.
 */
export function ConsoleProfile() {
  const { t } = useTranslation();
  return (
    <Page width="narrow" className="gap-6">
      <SettingsShell title={t("console.profile.title")} lead={t("console.profile.lead")}>
        <div className="flex min-w-0 flex-col divide-y divide-line">
          <PageSection id="identity" title={t("console.profile.identity")}>
            <Identity />
          </PageSection>
          <PageSection id="security" title={t("accountSettings.sections.security")}>
            <div className="flex flex-col gap-8">
              <Authenticator />
              <Passkeys />
            </div>
          </PageSection>
        </div>
      </SettingsShell>
    </Page>
  );
}

/**
 * The operator's name (#391), editable, and their e-mail. The name lives in the operators'
 * directory: once saved, the session is read again so the operator menu shows it too.
 */
function Identity() {
  const { t } = useTranslation();
  const id = useId();
  const csrf = useCsrfHeaders();
  const queryClient = useQueryClient();
  const { name, email } = useOperator();
  const [value, setValue] = useState(name ?? "");
  const save = useMutation({
    mutationFn: async (next: string) =>
      ok(await account.profile.$patch({ json: { name: next } }, csrf)),
    onSuccess: ({ name: saved }) => {
      setValue(saved);
      void queryClient.invalidateQueries({ queryKey: ["session"] });
      void queryClient.invalidateQueries({ queryKey: consoleKeys.sessions });
    },
  });
  const parsed = operatorDisplayNameSchema.safeParse(value);
  const valid = parsed.success;
  // The name as the server stores it (the schema's normalisation).
  const next = parsed.success ? parsed.data : value;
  return (
    <form
      className="flex max-w-xl flex-col gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (valid && next !== name) {
          save.mutate(next);
        }
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-name`}>{t("console.profile.name")}</Label>
        <Input
          id={`${id}-name`}
          autoComplete="name"
          maxLength={OPERATOR_NAME_MAX}
          value={value}
          aria-describedby={`${id}-hint`}
          aria-invalid={save.isError || (value !== "" && !valid) ? true : undefined}
          onChange={(e) => {
            save.reset();
            setValue(e.currentTarget.value);
          }}
        />
        <p id={`${id}-hint`} className="text-xs text-fg-muted">
          {t("console.profile.nameHint")}
        </p>
      </div>
      {value !== "" && !valid ? (
        <InlineError>{t("console.profile.nameInvalid")}</InlineError>
      ) : null}
      {save.isError ? (
        <InlineError>
          {save.error instanceof ApiError && save.error.status === 400
            ? t("console.profile.nameInvalid")
            : save.error instanceof ApiError
              ? save.error.message
              : t("errors.network")}
        </InlineError>
      ) : null}
      <p className="text-sm text-fg-secondary">
        <span className="text-fg-muted">{t("console.profile.email")}</span> {email ?? "—"}
      </p>
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={save.isPending || !valid || next === name}>
          {save.isPending ? t("app.saving") : t("app.save")}
        </Button>
        <span role="status" className="text-sm text-success-fg">
          {save.isSuccess ? t("console.profile.saved") : null}
        </span>
      </div>
    </form>
  );
}

/**
 * The operators' directory requires an authenticator: a signed-in operator always has one. A
 * lost one is reset by an administrator (`operator:add --reset-mfa`), never from here.
 */
function Authenticator() {
  const { t } = useTranslation();
  return (
    <div className="flex max-w-xl flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <SubHeading>{t("totp.title")}</SubHeading>
        <Badge variant="secondary">{t("totp.on")}</Badge>
      </div>
      <p className="text-sm leading-relaxed text-fg-secondary">{t("console.profile.totpLead")}</p>
    </div>
  );
}

function Passkeys() {
  const { t } = useTranslation();
  const id = useId();
  const csrf = useCsrfHeaders();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: consoleKeys.passkeys,
    queryFn: async (): Promise<OperatorPasskeysResponse> => ok(await account.passkeys.$get()),
    retry: false,
  });
  const stepUp = useSecureAction();
  const { calls, hasPasskey } = useOperatorStepUp();
  // What the step-up is for: an add takes the password and code only (#389), never a passkey.
  const [purpose, setPurpose] = useState<"add" | "change">("change");
  const panel = useRef<HTMLDivElement>(null);
  // Where focus goes back after the step-up: the control that asked for it, while it exists.
  const opener = useRef<HTMLElement | null>(null);
  // Read on the click, before a dialog moves focus.
  const noteOpener = () => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  };
  const [error, setError] = useState<string | null>(null);
  // Each change consumes the step-up, so the list hides again: the confirmation stays here.
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: consoleKeys.passkeys });

  // A change the server refuses for want of a fresh step-up goes back to `secure`, which asks
  // for one and runs it again; any other failure shows here.
  const guarded = (work: () => Promise<string | null>) => async () => {
    setBusy(true);
    setError(null);
    try {
      const message = await work();
      setStatus(message);
      await refresh();
    } catch (e) {
      if (needsReauth(e)) {
        throw e;
      }
      setError(
        e instanceof ApiError
          ? e.message
          : // fetch fails with a TypeError; the ceremony with a DOMException or no credential.
            e instanceof TypeError
            ? t("errors.network")
            : t("passkeys.cancelled"),
      );
    } finally {
      setBusy(false);
    }
  };
  const act = (work: () => Promise<string | null>) => {
    noteOpener();
    void stepUp.secure(guarded(work));
  };
  // After a step-up the ceremony waits for the person's click (#354).
  const adder = usePasskeyAdd();
  const add = () => {
    setStatus(null);
    setPurpose("add");
    const run = adder.action({
      options: async () =>
        (await ok(await account.passkeys.options.$post(undefined, csrf))).options,
      register: async (options) => {
        const credential = await createPasskey(options);
        await noContent(await account.passkeys.$post({ json: { credential } }, csrf));
      },
    });
    act(async () => ((await run()) ? t("passkeys.added") : null));
  };
  const remove = (passkey: Passkey) => {
    // A change consumes the step-up: options waiting for "Add the passkey" go too.
    adder.clear();
    setPurpose("change");
    act(async () => {
      await noContent(await account.passkeys[":id"].$delete({ param: { id: passkey.id } }, csrf));
      return t("passkeys.removed", { name: passkeyName(passkey, t) });
    });
  };

  const heading = <SubHeading id={`${id}-heading`}>{t("passkeys.title")}</SubHeading>;
  const body = query.isPending ? (
    <Skeleton className="h-24 w-full max-w-xl" />
  ) : query.isError ? (
    <div className="flex max-w-xl flex-col gap-3">
      {heading}
      <InlineError>{query.error.message}</InlineError>
    </div>
  ) : !query.data.available ? (
    <div className="flex max-w-xl flex-col gap-3">
      {heading}
      <Notice>{t("console.profile.unavailable")}</Notice>
    </div>
  ) : (
    <PasskeysPanel
      heading={heading}
      lead={t("console.profile.passkeysLead")}
      passkeys={query.data.passkeys}
      busy={busy}
      error={error}
      status={status}
      addReady={adder.ready}
      onReveal={() => {
        noteOpener();
        setStatus(null);
        setPurpose("change");
        stepUp.ask();
      }}
      onAdd={add}
      onRemove={remove}
    />
  );
  // The step-up stays mounted whatever the list's query does (a failed refetch keeps the form).
  return (
    <div ref={panel} className="contents">
      {body}
      <StepUpFrame
        open={stepUp.asking}
        onClose={stepUp.cancel}
        description={
          purpose === "add" ? t("console.profile.stepUpAdd") : t("console.profile.stepUp")
        }
        returnFocus={() =>
          opener.current?.isConnected
            ? opener.current
            : // The control that opened it went (Reveal turns into the list): the block's first.
              (panel.current?.querySelector<HTMLElement>("button") ?? null)
        }
      >
        <StepUpForm
          // A new form per purpose: the passkey choice never lingers into an add.
          key={purpose}
          calls={calls}
          offerPasskey={hasPasskey && purpose !== "add"}
          passkeyFirst
          onClose={stepUp.cancel}
          onDone={() => {
            const action = stepUp.take();
            void refresh();
            if (action) {
              void stepUp.secure(action);
            }
          }}
        />
      </StepUpFrame>
    </div>
  );
}
